import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../componentes/modal/modal";
import Button from "../../../../componentes/button/button";
import RateioHistoricoService from "../../../../services/resource/rateioHistoricoService";
import { formatarMoeda, formatarData } from "../../../../utils/solicitacoes/formatters";

const rateioHistoricoService = new RateioHistoricoService();

/**
 * Consulta as últimas solicitações finalizadas do mesmo fornecedor (Lançamento de Despesa e
 * Confirmação de Recebimento de Compras) que tiveram rateio, para reaproveitar o PADRÃO PERCENTUAL
 * de distribuição entre Contas BP — os valores em R$ da solicitação atual são recalculados a
 * partir do percentual, não copiados em absoluto.
 */
export default function ImportarRateioModal({ isOpen, onClose, fornecedorId, onImportar }) {
    const [historico, setHistorico] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [solicitacaoVisualizadaId, setSolicitacaoVisualizadaId] = useState(null);

    useEffect(() => {
        if (!isOpen || !fornecedorId) return undefined;
        let cancelado = false;
        setCarregando(true);
        setSolicitacaoVisualizadaId(null);

        rateioHistoricoService.listarPorFornecedor(fornecedorId)
            .then(res => { if (!cancelado) setHistorico(res.data); })
            .catch(() => {
                if (!cancelado) {
                    toast.error("Erro ao buscar o histórico de rateios do fornecedor.");
                    setHistorico([]);
                }
            })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [isOpen, fornecedorId]);

    function fechar() {
        setHistorico([]);
        setSolicitacaoVisualizadaId(null);
        onClose();
    }

    function utilizar(solicitacao) {
        onImportar(solicitacao);
        toast.success(`Rateio importado — ${solicitacao.itens.length} linha(s) substituíram o rateio atual.`);
        fechar();
    }

    const solicitacaoVisualizada = historico.find(s => s.solicitacaoId === solicitacaoVisualizadaId) ?? null;
    const totalRateadoVisualizado = solicitacaoVisualizada
        ? solicitacaoVisualizada.itens.reduce((soma, item) => soma + (Number(item.valorRateado) || 0), 0)
        : 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={fechar}
            title="Importar Rateio"
            subtitle="Últimas 5 solicitações finalizadas deste fornecedor com rateio — os valores serão recalculados pelo percentual sobre o valor total atual."
            className="w-[90%] min-h-[70vh]"
            footer={
                <div className="flex justify-end">
                    <Button variant="outline" label="Fechar" onClick={fechar} />
                </div>
            }
        >
            <div className="p-3 py-4 flex flex-col gap-4">
                {carregando && (
                    <div className="text-sm text-muted-foreground text-center py-6">Buscando histórico de rateios...</div>
                )}

                {!carregando && historico.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">
                        Nenhuma solicitação anterior com rateio encontrada para este fornecedor.
                    </div>
                )}

                {!carregando && historico.length > 0 && (
                    <div className="overflow-x-auto border border-border rounded-lg">
                        <table className="w-full text-xs">
                            <thead className="bg-muted sticky top-0">
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 font-semibold">Nº Solicitação</th>
                                    <th className="px-3 py-2 font-semibold">Processo</th>
                                    <th className="px-3 py-2 font-semibold">Data</th>
                                    <th className="px-3 py-2 font-semibold">Descrição</th>
                                    <th className="px-3 py-2 font-semibold text-right">Valor Total</th>
                                    <th className="px-3 py-2 font-semibold text-right">Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historico.map(solicitacao => (
                                    <tr
                                        key={solicitacao.solicitacaoId}
                                        className={`border-t border-border ${solicitacaoVisualizadaId === solicitacao.solicitacaoId ? "bg-primary/5" : ""}`}
                                    >
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{solicitacao.numero}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{solicitacao.processoLabel}</td>
                                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{formatarData(solicitacao.dataSolicitacao)}</td>
                                        <td className="px-3 py-2 text-foreground">{solicitacao.descricao}</td>
                                        <td className="px-3 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(solicitacao.valorTotal)}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="outline"
                                                    label="Visualizar Rateio"
                                                    onClick={() => setSolicitacaoVisualizadaId(atual => atual === solicitacao.solicitacaoId ? null : solicitacao.solicitacaoId)}
                                                />
                                                <Button
                                                    variant="blue"
                                                    label="Utilizar Rateio"
                                                    onClick={() => utilizar(solicitacao)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {solicitacaoVisualizada && (
                    <div className="border border-border rounded-lg p-3 flex flex-col gap-3">
                        <h4 className="text-sm font-semibold text-foreground">
                            Rateio da solicitação {solicitacaoVisualizada.numero}
                        </h4>
                        <div className="overflow-x-auto border border-border rounded-lg">
                            <table className="w-full text-xs">
                                <thead className="bg-muted">
                                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                        <th className="px-3 py-2 font-semibold">Nota Fiscal</th>
                                        <th className="px-3 py-2 font-semibold">Filial</th>
                                        <th className="px-3 py-2 font-semibold">Centro de Custo</th>
                                        <th className="px-3 py-2 font-semibold">Conta Contábil</th>
                                        <th className="px-3 py-2 font-semibold text-right">Percentual</th>
                                        <th className="px-3 py-2 font-semibold text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {solicitacaoVisualizada.itens.map((item, index) => (
                                        <tr key={`${item.baseBpId}-${index}`} className="border-t border-border">
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.numeroNotaFiscal}</td>
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.filialNome}</td>
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.centroCustoCodigo} · {item.centroCustoDescricao}</td>
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{item.contaReduzido} · {item.contaContabilDescricao}</td>
                                            <td className="px-3 py-2 text-right text-foreground whitespace-nowrap">{(Number(item.percentual) * 100).toFixed(1)}%</td>
                                            <td className="px-3 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(item.valorRateado)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Valor total da solicitação original: <strong className="text-foreground">{formatarMoeda(solicitacaoVisualizada.valorTotal)}</strong></span>
                            <span>Valor total rateado: <strong className="text-foreground">{formatarMoeda(totalRateadoVisualizado)}</strong></span>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
